import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Polygon, useMapEvents, useMap } from "react-leaflet";
import { Button } from "../../../core/components/Button";
import { api } from "../../../lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, MapPin, Trash2, Undo2, Search, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";

// Leaflet Icon Fix
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface LatLng {
    lat: number;
    lng: number;
}

// Map Controller to handle view changes
function MapController({ center, onMapClick }: { center: [number, number], onMapClick?: () => void }) {
    const map = useMap();
    useEffect(() => {
        map.setView(center);
    }, [center, map]);

    useMapEvents({
        click: () => {
            if (onMapClick) onMapClick();
        }
    });

    return null;
}

// Marker Component with Drag Support
function LocationMarker({ position, setPosition, mode }: any) {
    useMapEvents({
        click(e) {
            if (mode === "marker") {
                setPosition(e.latlng);
            }
        },
    });

    return position === null ? null : (
        <Marker
            position={position}
            draggable={mode === "marker"}
            eventHandlers={{
                dragend: (e) => {
                    setPosition(e.target.getLatLng());
                },
            }}
        />
    );
}

// Boundary Drawer Component
function BoundaryDrawer({ points, setPoints, mode }: any) {
    useMapEvents({
        click(e) {
            if (mode === "boundary") {
                setPoints((prev: any) => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }]);
            }
        },
        contextmenu() {
            // Optional: right click to remove last point?
            // For now we keep it simple with Undo button
        }
    });

    return points.length > 0 ? (
        <>
            <Polygon positions={points.map((p: any) => [p.lat, p.lng] as [number, number])} pathOptions={{ color: "blue" }} />
            {mode === "boundary" && points.map((p: any, idx: number) => (
                <Marker
                    key={idx}
                    position={p}
                    icon={L.divIcon({ className: 'bg-blue-500 rounded-full w-3 h-3 border-2 border-white', iconSize: [12, 12] })}
                />
            ))}
        </>
    ) : null;
}

export default function AdminProjectMapPage({ type = "project" }: { type?: "project" | "unit" }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // State
    const [lat, setLat] = useState<number | null>(null);
    const [lng, setLng] = useState<number | null>(null);
    const [boundary, setBoundary] = useState<LatLng[]>([]);
    const [mode, setMode] = useState<"marker" | "boundary">("marker");

    // Search State
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [mapCenter, setMapCenter] = useState<[number, number]>([30.0444, 31.2357]); // Default Cairo

    // Fetch Entity
    const { data: entity, isLoading } = useQuery({
        queryKey: [`${type}-map`, id],
        queryFn: () => api.get(`/api/admin/${type}s/${id}`).then(res => res.data.data),
        enabled: !!id,
    });

    // Update state when data loads
    useEffect(() => {
        if (entity) {
            setLat(entity.latitude);
            setLng(entity.longitude);

            if (entity.latitude && entity.longitude) {
                setMapCenter([entity.latitude, entity.longitude]);
            }

            let initialBoundary = entity.boundary || [];
            if (typeof initialBoundary === 'string') {
                try {
                    initialBoundary = JSON.parse(initialBoundary);
                } catch (e) {
                    initialBoundary = [];
                }
            }
            setBoundary(initialBoundary);
        }
    }, [entity]);

    // Save Mutation
    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/${type}s/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`${type}-map`, id] });
            queryClient.invalidateQueries({ queryKey: [`${type}s`] }); // Refresh list
            toast.success("Location settings saved successfully");
            navigate(`/admin/${type}s`);
        },
        onError: () => toast.error("Failed to save location settings")
    });

    const handleSave = () => {
        updateMutation.mutate({
            latitude: lat,
            longitude: lng,
            boundary: boundary
        });
    };

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        setIsSearching(true);
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
            const data = await response.json();
            setSearchResults(data);
        } catch (error) {
            console.error("Search failed:", error);
            toast.error("Failed to search location");
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectLocation = (result: any) => {
        const newLat = parseFloat(result.lat);
        const newLng = parseFloat(result.lon);
        setMapCenter([newLat, newLng]);
        setSearchResults([]); // Clear results
    };

    if (isLoading) return <div className="p-8">Loading data...</div>;
    if (!entity) return <div className="p-8">Data not found</div>;

    return (
        <div className="h-screen flex flex-col bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b px-6 py-4 flex items-center justify-between shadow-sm z-10">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(`/admin/${type}s`)} className="p-2 hover:bg-slate-100 rounded-full">
                        <ArrowLeft size={20} className="text-slate-600" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-slate-800">Map Editor: {entity.title || entity.slug}</h1>
                        <p className="text-sm text-slate-500">Set {type} location pin and boundary polygon</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={() => navigate(`/admin/${type}s`)}>Cancel</Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={updateMutation.isPending}
                        className="bg-secondary hover:bg-secondary-highlight text-white flex items-center gap-2"
                    >
                        <Save size={18} />
                        {updateMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                </div>
            </div>

            {/* Toolbar & Map */}
            <div className="flex-1 relative">
                {/* Floating Toolbar */}
                <div className="absolute top-4 left-14 z-[9999] flex flex-col gap-4">

                    {/* Search Box */}
                    <div className="bg-white p-2 rounded-lg shadow-lg border border-slate-200 w-72">
                        <form onSubmit={handleSearch} className="flex items-center gap-2 border-b border-slate-100 pb-2 mb-2">
                            <input
                                type="text"
                                placeholder="Search location (e.g. Cairo)"
                                className="flex-1 text-sm outline-none px-2"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <button type="submit" className="p-1 hover:bg-slate-100 rounded text-slate-500" disabled={isSearching}>
                                {isSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                            </button>
                        </form>

                        {/* Search Results */}
                        {searchResults.length > 0 && (
                            <div className="max-h-60 overflow-y-auto bg-white rounded-b-lg shadow-lg border-t border-slate-100 divide-y divide-slate-50">
                                {searchResults.map((result: any, idx: number) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleSelectLocation(result)}
                                        className="block w-full text-left text-sm py-3 px-4 hover:bg-slate-50 leading-normal text-slate-700 transition-colors"
                                        title={result.display_name}
                                    >
                                        {result.display_name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Tools */}
                    <div className="bg-white p-2 rounded-lg shadow-lg border border-slate-200 flex flex-col gap-2 w-48">
                        <div className="text-xs font-semibold text-slate-400 uppercase px-2 mb-1">Tools</div>

                        <button
                            onClick={() => setMode("marker")}
                            className={`flex items-center gap-3 px-3 py-2 rounded-md transition text-sm font-medium ${mode === "marker" ? "bg-secondary/10 text-secondary" : "hover:bg-slate-50 text-slate-600"}`}
                        >
                            <MapPin size={18} />
                            Set Pin
                        </button>

                        <button
                            onClick={() => setMode("boundary")}
                            className={`flex items-center gap-3 px-3 py-2 rounded-md transition text-sm font-medium ${mode === "boundary" ? "bg-secondary/10 text-secondary" : "hover:bg-slate-50 text-slate-600"}`}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h7v7H3z" /><path d="M14 3h7v7h-7z" /><path d="M14 14h7v7h-7z" /><path d="M3 14h7v7H3z" /></svg>
                            Draw Boundary
                        </button>

                        <div className="h-px bg-slate-100 my-1"></div>

                        <button
                            onClick={() => setBoundary(prev => prev.slice(0, -1))}
                            disabled={boundary.length === 0}
                            className="flex items-center gap-3 px-3 py-2 rounded-md text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                            <Undo2 size={18} />
                            Undo Point
                        </button>

                        <button
                            onClick={() => setBoundary([])}
                            disabled={boundary.length === 0}
                            className="flex items-center gap-3 px-3 py-2 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                            <Trash2 size={18} />
                            Clear Boundary
                        </button>
                    </div>
                </div>

                {/* Info Panel */}
                <div className="absolute bottom-4 left-4 z-[1000] bg-white/90 backdrop-blur px-4 py-2 rounded-lg shadow border border-slate-200 text-sm text-slate-600 max-w-md">
                    {mode === "marker"
                        ? "Click anywhere on the map to place the location pin. You can also drag the pin."
                        : "Click on the map to add points to the boundary polygon. Connect at least 3 points."
                    }
                </div>

                <MapContainer center={mapCenter} zoom={13} className="h-full w-full bg-slate-100">
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <LocationMarker position={lat && lng ? [lat, lng] : null} setPosition={(pos: any) => { setLat(pos.lat); setLng(pos.lng); }} mode={mode} />
                    <BoundaryDrawer points={boundary} setPoints={setBoundary} mode={mode} />
                    <MapController center={mapCenter} onMapClick={() => setSearchResults([])} />
                </MapContainer>
            </div>
        </div>
    );
}
